import axios from 'axios';
import { Budget, Transaction, CreateBudgetInput, AuthResponse, VerificationResponse, UserResponse, TransactionFilters, PaginatedTransactionResponse } from '../types';

// Configure axios defaults
const apiUrl = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : 'https://personal-finance-production-0ca3.up.railway.app');
console.log('=== API Configuration Debug ===');
console.log('process.env.REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
console.log('Final apiUrl:', apiUrl);
console.log('process.env.NODE_ENV:', process.env.NODE_ENV);
console.log('All process.env keys starting with REACT_APP:', 
  Object.keys(process.env).filter(key => key.startsWith('REACT_APP')));
console.log('================================');

axios.defaults.baseURL = apiUrl;
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.withCredentials = true; // Required for CSRF

// Create a separate axios instance for CSRF requests - using the same baseURL as main axios
const csrfAxios = axios.create({
  baseURL: apiUrl, // Use the same baseURL as the main axios instance
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

console.log('=== Axios Configuration ===');
console.log('axios.defaults.baseURL:', axios.defaults.baseURL);
console.log('csrfAxios.defaults.baseURL:', csrfAxios?.defaults?.baseURL);
console.log('============================');

// Simple CSRF token cache
let csrfTokenCache: { token: string; timestamp: number } | null = null;
const CSRF_TOKEN_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Function to get CSRF token with retry logic
const getCsrfToken = async (retries = 3): Promise<string | null> => {
  // In development mode, if CSRF is causing issues, allow bypassing
  if (process.env.NODE_ENV === 'development') {
    console.log('Development mode: Attempting to get CSRF token...');
  }
  
  console.log('Getting CSRF token...', {
    baseURL: csrfAxios?.defaults?.baseURL,
    apiUrl,
    isDevelopment: process.env.NODE_ENV === 'development'
  });
  
  // Check cache first
  if (csrfTokenCache && (Date.now() - csrfTokenCache.timestamp) < CSRF_TOKEN_CACHE_DURATION) {
    console.log('Using cached CSRF token');
    return csrfTokenCache.token;
  }

  for (let i = 0; i < retries; i++) {
    try {
      console.log(`CSRF token attempt ${i + 1}/${retries} to ${apiUrl}/api/csrf-token`);
      if (!csrfAxios) {
        throw new Error('csrfAxios not available in test environment');
      }
      const { data } = await csrfAxios.get<{ csrfToken: string }>('/api/csrf-token');
      console.log('CSRF response:', data);
      if (data.csrfToken) {
        // Cache the token
        csrfTokenCache = {
          token: data.csrfToken,
          timestamp: Date.now()
        };
        console.log('Successfully obtained CSRF token');
        return data.csrfToken;
      }
    } catch (error: any) {
      console.error(`CSRF token fetch attempt ${i + 1} failed:`, {
        status: error.response?.status,
        data: error.response?.data,
        headers: error.response?.headers,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          headers: error.config?.headers,
          fullUrl: error.config?.baseURL + error.config?.url
        },
        message: error.message
      });
      
      // In development mode, be more lenient with CSRF errors
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Development mode: CSRF token fetch failed (${error.message}), but continuing...`);
        
        // If this is the last attempt in dev mode, return a mock token or null
        if (i === retries - 1) {
          console.warn('Development mode: All CSRF attempts failed, allowing request without CSRF token');
          return null; // Allow the request to proceed without CSRF token
        }
      }
      
      // If it's a 429 error, wait longer before retrying
      if (error.response?.status === 429) {
        if (i === retries - 1) {
          throw new Error('Rate limited: Too many CSRF token requests. Please try again later.');
        }
        // Wait longer for rate limit errors (15-30 seconds)
        const delay = 15000 + (Math.random() * 15000); // 15-30 seconds
        console.log(`Rate limited, waiting ${Math.round(delay/1000)} seconds before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        if (i === retries - 1) {
          // In production, this is a hard error
          if (process.env.NODE_ENV === 'production') {
            throw new Error('Failed to fetch CSRF token after multiple attempts');
          } else {
            // In development, log the error but don't throw
            console.warn('Development mode: Failed to fetch CSRF token, but allowing request to proceed');
            return null;
          }
        }
        // Standard exponential backoff for other errors
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
      }
    }
  }
  return null;
};

// Add token to requests if it exists
axios.interceptors.request.use(async function(config: any) {
  try {
    // Add Authorization token if it exists
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token for non-GET requests (unless explicitly skipped)
    if (config.method?.toLowerCase() !== 'get' && !(config as any).skipCsrf) {
      try {
        const csrfToken = await getCsrfToken();
        if (csrfToken && config.headers) {
          config.headers['x-csrf-token'] = csrfToken;
        } else if (process.env.NODE_ENV === 'development') {
          console.warn('Development mode: Proceeding without CSRF token');
        }
      } catch (csrfError) {
        console.error('CSRF token fetch failed:', csrfError);
        // In development mode, allow the request to proceed without CSRF token
        if (process.env.NODE_ENV === 'development') {
          console.warn('Development mode: CSRF token failed, proceeding without it');
        } else {
          // In production, reject the request if CSRF token can't be obtained
          return Promise.reject(new Error('Failed to obtain CSRF token'));
        }
      }
    }

    return config;
  } catch (error) {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
});

// Add response interceptor to handle errors
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 423) {
      // Account is locked
      localStorage.removeItem('token');
      window.location.href = '/login?error=account_locked';
    } else if (error.response?.status === 429 && !error.config._retryCount) {
      // Rate limit error, implement exponential backoff retry
      error.config._retryCount = (error.config._retryCount || 0) + 1;
      
      if (error.config._retryCount <= 3) {
        const delay = Math.min(1000 * Math.pow(2, error.config._retryCount - 1), 30000); // Max 30 seconds
        console.log(`Rate limited, retrying in ${delay/1000} seconds...`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return axios(error.config);
      }
    } else if (error.response?.status === 403 && !error.config._retry) {
      // CSRF token issue, retry once with a fresh token
      error.config._retry = true;
      try {
        const csrfToken = await getCsrfToken();
        if (csrfToken && error.config.headers) {
          error.config.headers['x-csrf-token'] = csrfToken;
          return axios(error.config);
        }
      } catch (csrfError) {
        console.error('Failed to refresh CSRF token:', csrfError);
      }
    }
    return Promise.reject(error);
  }
);

// Auth service
export const auth = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    try {
      console.log('Making login request...');
      // Get CSRF token before login attempt
      const csrfToken = await getCsrfToken();
      if (!csrfToken) {
        throw new Error('Failed to get CSRF token');
      }

      const response = await axios.post<AuthResponse>('/api/auth/login', 
        { email, password },
        { 
          headers: { 'x-csrf-token': csrfToken }
        }
      );

      console.log('Login response:', response.data);
      if (!response.data.token) {
        console.error('No token in response:', response.data);
        throw new Error('No token received from server');
      }
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 429) {
        throw new Error('Too many login attempts. Please try again later.');
      }
      if (error.response?.status === 401 && error.response?.data?.msg?.includes('verify')) {
        throw new Error('Please verify your email address before logging in.');
      }
      throw error;
    }
  },
  devLogin: async (): Promise<AuthResponse> => {
    try {
      console.log('Making development login request...');
      // Get CSRF token before dev login attempt
      const csrfToken = await getCsrfToken();
      if (!csrfToken) {
        throw new Error('Failed to get CSRF token');
      }

      const response = await axios.post<AuthResponse>('/api/auth/dev-login', 
        {},
        { 
          headers: { 'x-csrf-token': csrfToken }
        }
      );

      console.log('Dev login response:', response.data);
      if (!response.data.token) {
        console.error('No token in dev login response:', response.data);
        throw new Error('No token received from server');
      }
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Development login is not available in production');
      }
      console.error('Dev login error:', error);
      throw error;
    }
  },
  register: async (name: string, email: string, password: string): Promise<AuthResponse> => {
    try {
      console.log('Making registration request...');
      // Get CSRF token before registration attempt
      const csrfToken = await getCsrfToken();
      if (!csrfToken) {
        throw new Error('Failed to get CSRF token');
      }

      console.log('Making registration request to:', `${apiUrl}/api/auth/register`);
      const response = await axios.post<AuthResponse>(
        '/api/auth/register', 
        { name, email, password },
        { 
          headers: { 'x-csrf-token': csrfToken }
        }
      );

      console.log('Registration response:', response.data);
      if (!response.data.token) {
        console.error('No token in response:', response.data);
        throw new Error('No token received from server');
      }
      return response.data;
    } catch (error: any) {
      console.error('Registration error:', {
        status: error.response?.status,
        data: error.response?.data,
        headers: error.response?.headers,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          headers: error.config?.headers,
          fullUrl: error.config?.baseURL + error.config?.url
        }
      });
      if (error.response?.status === 429) {
        throw new Error('Too many registration attempts. Please try again later.');
      }
      if (error.response?.data?.errors) {
        const passwordError = error.response.data.errors.find(
          (err: any) => err.param === 'password'
        );
        if (passwordError) {
          throw new Error(passwordError.msg);
        }
      }
      throw error;
    }
  },
  verifyEmail: async (token: string): Promise<VerificationResponse> => {
    try {
      const response = await axios.post<VerificationResponse>('/api/auth/verification/verify-email', { token });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 429) {
        throw new Error('Too many verification attempts. Please try again later.');
      }
      throw new Error(error.response?.data?.message || 'Failed to verify email');
    }
  },
  resendVerification: async (email: string): Promise<VerificationResponse> => {
    try {
      const response = await axios.post<VerificationResponse>('/api/auth/verification/send-verification', { email });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 429) {
        throw new Error('Too many verification email requests. Please try again later.');
      }
      throw new Error(error.response?.data?.message || 'Failed to resend verification email');
    }
  },
  requestPasswordReset: async (email: string): Promise<VerificationResponse> => {
    try {
      const response = await axios.post<VerificationResponse>('/api/auth/password-reset/request', { email });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 429) {
        throw new Error('Too many password reset requests. Please try again later.');
      }
      throw new Error(error.response?.data?.message || 'Failed to send reset email');
    }
  },
  verifyResetToken: async (token: string): Promise<VerificationResponse> => {
    try {
      const response = await axios.post<VerificationResponse>('/api/auth/password-reset/verify', { token });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 429) {
        throw new Error('Too many verification attempts. Please try again later.');
      }
      throw new Error(error.response?.data?.message || 'Invalid or expired reset token');
    }
  },
  resetPassword: async (token: string, password: string): Promise<VerificationResponse> => {
    try {
      const response = await axios.post<VerificationResponse>('/api/auth/password-reset/reset', { token, password });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 429) {
        throw new Error('Too many password reset attempts. Please try again later.');
      }
      throw new Error(error.response?.data?.message || 'Failed to reset password');
    }
  },
  changePassword: async (currentPassword: string, newPassword: string): Promise<VerificationResponse> => {
    try {
      const response = await axios.post<VerificationResponse>('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Current password is incorrect');
      }
      throw new Error(error.response?.data?.message || 'Failed to change password');
    }
  },
  getUser: async (): Promise<UserResponse> => {
    const response = await axios.get<UserResponse>('/api/auth/user');
    return response.data;
  }
};

// Support service
export const support = {
  sendContactEmail: async (contactData: { email: string; subject: string; message: string }): Promise<{ message: string }> => {
    try {
      const response = await axios.post<{ message: string }>('/api/support/contact', contactData, {
        skipCsrf: true // Skip CSRF token for public support contact form
      } as any);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 429) {
        throw new Error('Too many contact attempts. Please try again later.');
      }
      if (error.response?.status === 400) {
        // If there are specific validation errors, show them
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          const errorMessages = error.response.data.errors.map((err: any) => err.msg).join('\n');
          throw new Error(errorMessages);
        }
        throw new Error(error.response?.data?.message || 'Please check your input and try again.');
      }
      throw new Error(error.response?.data?.message || 'Failed to send message. Please try again.');
    }
  },
};

// Utility function for exponential backoff
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const retryWithBackoff = async (fn: () => Promise<any>, maxRetries = 3, baseDelay = 1000) => {
  let retries = 0;
  while (true) {
    try {
      return await fn();
    } catch (error: any) {
      if (error.response?.status !== 429 || retries >= maxRetries) {
        throw error;
      }
      
      // Exponential backoff with jitter
      const exponentialDelay = baseDelay * Math.pow(2, retries);
      const jitter = Math.random() * 1000; // Add up to 1 second of jitter
      const delay = Math.min(exponentialDelay + jitter, 30000); // Cap at 30 seconds
      
      console.log(`Rate limited, retrying in ${Math.round(delay/1000)} seconds... (attempt ${retries + 1}/${maxRetries})`);
      await wait(delay);
      retries++;
    }
  }
};

// Budget service
export const budgets = {
  getAll: async () => {
    console.log('API: budgets.getAll() called');
    return retryWithBackoff(async () => {
      try {
        console.log('API: Making request to GET /api/budgets');
        const response = await axios.get<Budget[]>('/api/budgets');
        console.log('API: budgets.getAll() response:', response.data.length, 'budgets');
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 429) {
          throw error; // Let retryWithBackoff handle it
        }
        console.error('Error fetching budgets:', error);
        throw new Error('Failed to fetch budgets. Please try again later.');
      }
    });
  },
  getByMonth: async (month: string | Date) => {
    console.log('API: budgets.getByMonth() called with month:', month);
    return retryWithBackoff(async () => {
      try {
        const monthParam = month instanceof Date ? month.toISOString() : month;
        console.log('API: Making request to GET /api/budgets?month=' + monthParam);
        const response = await axios.get<Budget[]>('/api/budgets', { 
          params: { month: monthParam } 
        });
        console.log('API: budgets.getByMonth() response:', response.data.length, 'budgets');
        console.log('API: budgets.getByMonth() raw response:', response.data);
        // Return the first budget (should only be one for a specific month)
        const budget = response.data[0] || null;
        console.log('API: budgets.getByMonth() returning:', budget ? 'budget found' : 'no budget');
        return budget;
      } catch (error: any) {
        if (error.response?.status === 429) {
          throw error; // Let retryWithBackoff handle it
        }
        console.error('Error fetching budget for month:', error);
        throw new Error('Failed to fetch budget for month. Please try again later.');
      }
    });
  },
  getAvailableMonths: async () => {
    console.log('API: budgets.getAvailableMonths() called');
    return retryWithBackoff(async () => {
      try {
        console.log('API: Making request to GET /api/budgets/months');
        const response = await axios.get<string[]>('/api/budgets/months');
        console.log('API: budgets.getAvailableMonths() response:', response.data.length, 'months');
        return response.data.map(month => new Date(month));
      } catch (error: any) {
        if (error.response?.status === 429) {
          throw error; // Let retryWithBackoff handle it
        }
        console.error('Error fetching available months:', error);
        throw new Error('Failed to fetch available months. Please try again later.');
      }
    });
  },
  getById: async (id: string) => {
    return retryWithBackoff(async () => {
      try {
        const response = await axios.get<Budget>(`/api/budgets/${id}`);
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 429) {
          throw error; // Let retryWithBackoff handle it
        }
        console.error('Error fetching budget:', error);
        throw new Error('Failed to fetch budget. Please try again later.');
      }
    });
  },
  create: async (budget: CreateBudgetInput) => {
    return retryWithBackoff(async () => {
      try {
        const response = await axios.post<Budget>('/api/budgets', budget);
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 429) {
          throw error; // Let retryWithBackoff handle it
        }
        console.error('Error creating budget:', error);
        throw new Error('Failed to create budget. Please try again later.');
      }
    });
  },
  update: async (id: string, budget: Partial<Budget>) => {
    return retryWithBackoff(async () => {
      try {
        const response = await axios.put<Budget>(`/api/budgets/${id}`, budget);
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 429) {
          throw error; // Let retryWithBackoff handle it
        }
        console.error('Error updating budget:', error);
        throw new Error('Failed to update budget. Please try again later.');
      }
    });
  },
  delete: async (id: string) => {
    return retryWithBackoff(async () => {
      try {
        const response = await axios.delete(`/api/budgets/${id}`);
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 429) {
          throw error; // Let retryWithBackoff handle it
        }
        console.error('Error deleting budget:', error);
        throw new Error('Failed to delete budget. Please try again later.');
      }
    });
  }
};

// Transaction service
export const transactions = {
  getAll: async (params?: TransactionFilters) => {
    return retryWithBackoff(async () => {
      try {
        const response = await axios.get<PaginatedTransactionResponse | Transaction[]>('/api/transactions', { params });
        
        // Handle backward compatibility - if response is array, convert to paginated format
        if (Array.isArray(response.data)) {
          return {
            transactions: response.data,
            pagination: {
              currentPage: 1,
              totalPages: 1,
              totalCount: response.data.length,
              hasNextPage: false,
              hasPrevPage: false,
              limit: response.data.length
            }
          } as PaginatedTransactionResponse;
        }
        
        return response.data as PaginatedTransactionResponse;
      } catch (error: any) {
        if (error.response?.status === 429) {
          throw error; // Let retryWithBackoff handle it
        }
        console.error('Error fetching transactions:', error);
        throw new Error('Failed to fetch transactions. Please try again later.');
      }
    });
  },
  getById: async (id: string) => {
    return retryWithBackoff(async () => {
      try {
        const response = await axios.get<Transaction>(`/api/transactions/${id}`);
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 429) {
          throw error; // Let retryWithBackoff handle it
        }
        console.error('Error fetching transaction:', error);
        throw new Error('Failed to fetch transaction. Please try again later.');
      }
    });
  },
  create: async (transaction: Omit<Transaction, '_id' | 'user' | 'createdAt'>) => {
    return retryWithBackoff(async () => {
      try {
        const response = await axios.post<Transaction>('/api/transactions', transaction);
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 429) {
          throw error; // Let retryWithBackoff handle it
        }
        console.error('Error creating transaction:', error);
        throw new Error('Failed to create transaction. Please try again later.');
      }
    });
  },
  update: async (id: string, transaction: Partial<Transaction>) => {
    return retryWithBackoff(async () => {
      try {
        const response = await axios.put<Transaction>(`/api/transactions/${id}`, transaction);
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 429) {
          throw error; // Let retryWithBackoff handle it
        }
        console.error('Error updating transaction:', error);
        throw new Error('Failed to update transaction. Please try again later.');
      }
    });
  },
  delete: async (id: string) => {
    return retryWithBackoff(async () => {
      try {
        const response = await axios.delete(`/api/transactions/${id}`);
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 429) {
          throw error; // Let retryWithBackoff handle it
        }
        console.error('Error deleting transaction:', error);
        throw new Error('Failed to delete transaction. Please try again later.');
      }
    });
  }
};

export default axios; 